import punycode from 'punycode';

export default class Validator {

    fields: Object;
    messages: Object;
    touched: Object;

    constructor() {
        this.fields = {};
        this.messages = {};
        this.touched = {};
    }

    add(name: string, methods: Array<ValidatorMethod>): Validator {
        if (!this.fields[name]) {
            this.messages[name] = [];
            this.fields[name] = [];
        }
        this.fields[name].push(methods);
        return this;
    }

    delete(name: string): Validator {
        if (this.fields[name]) {
            for (let i in this.fields[name]) {
                delete this.fields[name][i];
            }
        }
        if (this.messages[name]) {
            delete this.fields[name];
        }
        /*if (this.touched[name]) {
            delete this.fields[name];
        }*/

        return this;
    }

    isValid(prop = null) {
        if (prop) {
            return this.isFieldValid(prop);
        }

        for (let field in this.fields) {
            if (!this.isFieldValid(field)) {
                return false;
            }
        }

        return true;
    }

    isFieldValid(prop) {
        if (!this.fields[prop]) {
            return true;
        }

        this.messages[prop] = [];

        let valid = true;
        let res = null;

        for (let method of this.fields[prop]) {
            if (method instanceof ValidatorMethodAbstract && method.condition && !method.condition()) {
                continue;
            }

            res = method.validate();
            if (!res) {
                this.messages[prop].push({validator: method.constructor.name, message: method.getMessage()});
            }

            valid = valid && res;
        }

        return valid;
    }

    getMessages(prop): Array<string> {
        if (!this.messages[prop]) {
            return [];
        }
        return this.messages[prop];
    }

    setTouched(prop: string): void {
        this.touched[prop] = true;
    }

    isTouched(prop: string): boolean {
        return this.touched.hasOwnProperty(prop) && this.touched[prop] === true;
    }

    validate() {
        let isValid = true;
        for (const fieldName in this.fields) {
            for (const field of this.fields[fieldName]) {
                this.setTouched(fieldName);

                if (field instanceof ValidatorMethodAbstract && field.condition && !field.condition()) {
                    continue;
                }

                isValid = isValid && field.validate();
            }
        }

        return isValid;
    }

    scrollToErrors() {
        const invalidElements = document.querySelectorAll('.is-invalid');
        const elementTop = invalidElements[0].getBoundingClientRect().top + window.scrollY;
        const windowHeight = window.innerHeight;
        const offset = Math.round(windowHeight / 2) - (invalidElements[0].getBoundingClientRect().height / 2);
        window.scrollTo(0, elementTop - offset);
    }
}

interface ValidatorMethod {
    validate(value: any): Boolean

    setMessage(message: string): ValidatorMethod

    setCondition?(callback: () => boolean): ValidatorMethod
}

abstract class ValidatorMethodAbstract implements ValidatorMethod {
    message: string;
    obj: Proxy<Object>;
    prop: string;
    condition: (() => boolean) | null = null;

    setMessage(message: string): ValidatorMethodAbstract {
        this.message = message;
        return this;
    }

    setCondition(callback: () => boolean): ValidatorMethodAbstract {
        this.condition = callback;
        return this;
    }

    getMessage(): string {
        return this.message;
    }

    validate(): Boolean {
        return true;
    }

    watchChanges() {
        this.obj.$watch(this.prop, (n, o) => {
            this.validate();
        });
    }

    getValue() {
        if (!this.prop.includes('.')) {
            return this.obj[this.prop];
        }

        let parts = this.prop.split('.');
        let variable = `this.obj['${parts[0]}']` + parts.slice(1).map(p => `['${p}']`).join('');

        return eval(variable);
    }
}

export class MinLengthValidator extends ValidatorMethodAbstract {
    minLength: Number;
    isValid: boolean;

    constructor(obj: Proxy<Object>, prop: string, minLength: Number) {
        super();
        this.minLength = minLength;
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Minimalna długość wynosi ${minLength} znaków`);
        this.watchChanges();
    }

    validate(): Boolean {
        if (!this.getValue()) {
            return false;
        }
        this.isValid = this.getValue().toString().length >= this.minLength;
        return this.isValid;
    }
}

export class MaxLengthValidator extends ValidatorMethodAbstract {
    minLength: Number;
    isValid: boolean;

    constructor(obj: Proxy<Object>, prop: string, maxLength: Number) {
        super();
        this.maxLength = maxLength;
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Maksymalna długość wynosi ${maxLength} znaków`);
        this.watchChanges();
    }

    validate(): Boolean {
        if (!this.getValue()) {
            return true;
        }
        this.isValid = this.getValue().toString().length <= this.maxLength;
        return this.isValid;
    }
}

export class LengthValidator extends ValidatorMethodAbstract {
    minLength: Number;
    isValid: boolean;

    constructor(obj: Proxy<Object>, prop: string, minLength: Number, maxLength: Number) {
        super();
        this.minLength = minLength;
        this.maxLength = maxLength;
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Wymagana długość od ${minLength} do ${maxLength} znaków`);
        this.watchChanges();
    }

    validate(): Boolean {
        if (!this.getValue()) {
            return false;
        }
        this.isValid = this.getValue().toString().length >= this.minLength
            && this.getValue().toString().length <= this.maxLength;
        return this.isValid;
    }
}

export class RegularExpressionValidator extends ValidatorMethodAbstract {
    expression: string;

    constructor(obj: Proxy<Object>, prop: string, expression: string) {
        super();
        this.expression = expression;
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Ciąg znaków musi pasować do wyrażenia regularnego ${expression}`);
        this.watchChanges();
    }

    validate(): Boolean {
        let regExp = new RegExp(this.expression, 'g');
        return regExp.test(this.getValue());
    }
}

export class DateValidator extends RegularExpressionValidator {

    constructor(obj: Proxy<Object>, prop: string, nullable: Boolean) {
        super(obj, prop, '^[1-2][0-9][0-9][0-9]-[0-1][0-9]-[0-3][0-9]$');
        this.prop = prop;
        this.obj = obj;
        this.nullable = nullable;
        this.setMessage(`Nieprawidłowy format daty`);
        this.watchChanges();
    }

    validate(): Boolean {

        if (!this.getValue() && this.nullable) {
            return true;
        }

        if (!super.validate()) {
            return false;
        }

        let date = Date.parse(this.getValue());
        if (isNaN(date)) {
            return false;
        }

        date = new Date(this.getValue());
        let dateNow = new Date();

        if (date > dateNow) {
            return false;
        }

        return true;
    }
}

export class NumberValidator extends RegularExpressionValidator {

    constructor(obj: Proxy<Object>, prop: string, nullable: Boolean) {
        super(obj, prop, '^\\d+$');
        this.prop = prop;
        this.obj = obj;
        this.nullable = nullable;
        this.setMessage(`Wartość musi być liczbą`);
        this.watchChanges();
    }

    validate(): Boolean {

        if (!this.getValue() && this.nullable) {
            return true;
        }

        if (!super.validate()) {
            return false;
        }

        return true;
    }
}

export class TrueValidator extends ValidatorMethodAbstract {
    expression: RegExp;

    validate(): Boolean {
        return false;
    }
}

export class NotEmptyValidator extends ValidatorMethodAbstract {

    constructor(obj: Proxy<Object>, prop: string) {
        super();
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Pole nie może być puste`);
        this.watchChanges();
    }

    validate(): Boolean {
        if (!this.getValue()) {
            return false;
        }

        if (typeof (this.getValue()) === 'number') {
            return this.getValue() !== 0;
        }

        let value = this.getValue().trim();
        return value.length >= 1;
    }
}

export class IdNumberValidator extends ValidatorMethodAbstract {

    constructor(obj: Proxy<Object>, prop: string) {
        super();
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Nieprawidłowy numer NIP`);
        this.watchChanges();
    }

    validate(): Boolean {
        let value = this.getValue()
        value = value.replace(/\s|-|\./ig, '');

        let regex = new RegExp('^([A-Z]{2})?([0-9]{10})$', 'i');

        if (!regex.test(value)) {
            return false;
        }

        const idNumberMatches = regex.exec(value);
        const idNumber = idNumberMatches[2];
        const weight = [6, 5, 7, 2, 3, 4, 5, 6, 7];
        const controlNumber = parseInt(idNumber.substring(9, 10));
        const weightCount = weight.length;

        let sum = 0;
        for (let i = 0; i < weightCount; i++) {
            sum += (parseInt(idNumber.substr(i, 1)) * weight[i]);
        }

        return sum % 11 === controlNumber;
    }
}

export class EmailValidator extends ValidatorMethodAbstract {

    constructor(obj: Proxy<Object>, prop: string) {
        super();
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Nieprawidłowy adres e-mail`);
        this.watchChanges();
    }

    validate(): Boolean {
        let value = this.getValue()
        if (!value) {
            return false;
        }

        return value.match(
            /^([^\s\@])+\@(([^\s\@\.])+\.)+([^\s\.]{2,})+$/
        );
    }
}

export class HostnameValidator extends ValidatorMethodAbstract {

    constructor(obj: Proxy<Object>, prop: string) {
        super();
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Nieprawidłowy adres hosta`);
        this.watchChanges();
    }

    validate(): Boolean {
        const value = this.getValue();
        if (value.length === 0) {
            return true;
        }
        return /^[a-z0-9\.\-]{1,}[\.]{1}[a-z]{2,30}$/.test(this.getValue());
    }
}

export class DomainValidator extends ValidatorMethodAbstract {

    constructor(obj: Proxy<Object>, prop: string) {
        super();
        this.prop = prop;
        this.obj = obj;
        this.setMessage(`Nieprawidłowa nazwa domeny`);
        this.watchChanges();
    }

    validate(): Boolean {
        const value = this.getValue();
        if (value.length > 255 || value.length <= 1) {
            return false;
        }
        return /^\b((?=[a-zA-Z0-9-]{1,63}\.)(xn---*)?[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+([a-zA-Z]{2,63}|xn--[a-zA-Z0-9]{4,63})\b$/
            .test(punycode.toASCII(value))
    }
}
